import type { AxiosResponse } from 'axios';

import cache from './apiCache';
import endpoints from './endpoints/index';
import { performRequest, request, Request, resetApiCache } from './rest';

/** Manipulate endpoints so `resetCache` becomes a new property of the request function so it can be called consistently */
type Endpoint<T extends (...args: any) => any> = ((...args: Parameters<T>) => Promise<AxiosResponse<ReturnType<T>>>) & {
  resetCache: () => void;
};
type Endpoints<T extends Record<string, (args: any) => any>> = {
  [key in keyof T]: Endpoint<T[key]>;
};

const buildEndpoints = (): Endpoints<typeof endpoints> => {
  const assembledEndpoints = {} as any;
  for (const endpointKey in endpoints) {
    const key = endpointKey as keyof typeof endpoints;
    const endpoint = endpoints[key];

    // Execute the endpoint function to see what it returns
    const resultFromEndpoint = endpoint({} as any);

    // CHECK: If the result is a Promise, it's a mocked endpoint. Use it directly.
    if (resultFromEndpoint && typeof resultFromEndpoint.then === 'function') {
      assembledEndpoints[key] = endpoint; // Use the original mocked function
    } else {
      // It's a standard request config object, so wrap it with performRequest.
      const args = resultFromEndpoint as unknown as Request<any>;
      const newEndpoint = (userArgs: Request<any>) => performRequest({ ...args, ...userArgs } as any);
      newEndpoint.resetCache = () => resetApiCache(args);
      assembledEndpoints[key] = newEndpoint as Endpoint<typeof endpoint>;
    }
  }

  return assembledEndpoints;
};

const api = {
  request,
  endpoints: buildEndpoints(),
  cache,
};

export { api };
